import React from 'react';
import { RefreshCw, History, RotateCcw } from 'lucide-react';

// Admin dashboard "flags" tab (extracted from AdminPage).
export const AdminFlagsTab = ({
  featureFlags,
  flagCategoryFilter,
  flagEnvironment,
  flagHistory,
  formatDate,
  resetFeatureFlag,
  rollbackFeatureFlag,
  setFlagCategoryFilter,
  toggleFeatureFlag,
}) => (
  <div className="space-y-6">
    {/* Environment Banner */}
    <div className={`p-4 rounded-xl border ${
      flagEnvironment === 'production' 
        ? 'bg-red-500/10 border-red-500/30' 
        : flagEnvironment === 'staging'
          ? 'bg-yellow-500/10 border-yellow-500/30'
          : 'bg-green-500/10 border-green-500/30'
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${
            flagEnvironment === 'production' ? 'bg-red-500' : 
            flagEnvironment === 'staging' ? 'bg-yellow-500' : 'bg-green-500'
          }`} />
          <span className="font-semibold text-white capitalize">{flagEnvironment} Environment</span>
        </div>
        <span className="text-xs text-zinc-400">
          {Object.keys(featureFlags).length} flags configured
        </span>
      </div>
    </div>

    {/* Category Filter */}
    <div className="flex items-center gap-3">
      <span className="text-sm text-zinc-400">Filter by category:</span>
      {['all', 'admin', 'user', 'experimental', 'system'].map((cat) => (
        <button
          key={cat}
          onClick={() => setFlagCategoryFilter(cat)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            flagCategoryFilter === cat 
              ? 'bg-orange-500 text-white' 
              : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
          }`}
        >
          {cat.charAt(0).toUpperCase() + cat.slice(1)}
        </button>
      ))}
    </div>

    {/* Feature Flags List */}
    <div className="grid gap-4">
      {Object.entries(featureFlags)
        .filter(([_, flag]) => flagCategoryFilter === 'all' || flag.category === flagCategoryFilter)
        .map(([key, flag]) => (
        <div 
          key={key} 
          className={`bg-zinc-900/50 rounded-xl border p-5 ${
            flag.critical ? 'border-orange-500/30' : 'border-zinc-800'
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold text-white">{flag.name}</h4>
                {flag.critical && (
                  <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded">
                    Critical
                  </span>
                )}
                {flag.overridden && (
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">
                    Overridden
                  </span>
                )}
                <span className={`px-2 py-0.5 text-xs rounded ${
                  flag.category === 'admin' ? 'bg-purple-500/20 text-purple-400' :
                  flag.category === 'user' ? 'bg-green-500/20 text-green-400' :
                  flag.category === 'experimental' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-zinc-700 text-zinc-400'
                }`}>
                  {flag.category}
                </span>
              </div>
              <p className="text-sm text-zinc-500 mb-2">{flag.description}</p>
              <p className="text-xs text-zinc-600">
                Default: {flag.default ? 'Enabled' : 'Disabled'}
                {flag.updated_at && ` • Last changed: ${formatDate(flag.updated_at)}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {flag.overridden && (
                <>
                  <button
                    onClick={() => rollbackFeatureFlag(key)}
                    className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
                    title="Rollback to previous"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => resetFeatureFlag(key)}
                    className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
                    title="Reset to default"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </>
              )}
              <button
                onClick={() => toggleFeatureFlag(key, flag.enabled)}
                className={`relative w-14 h-8 rounded-full transition-colors ${
                  flag.enabled ? 'bg-green-500' : 'bg-zinc-700'
                }`}
              >
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform ${
                  flag.enabled ? 'left-7' : 'left-1'
                }`} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Change History */}
    <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden">
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2">
        <History className="w-5 h-5 text-zinc-400" />
        <h3 className="font-semibold">Recent Changes</h3>
      </div>
      {flagHistory.length === 0 ? (
        <div className="px-5 py-8 text-center text-zinc-500">
          <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No changes recorded yet</p>
        </div>
      ) : (
        <div className="divide-y divide-zinc-800 max-h-64 overflow-y-auto">
          {flagHistory.map((change, i) => (
            <div key={change.timestamp || `history-${i}`} className="px-5 py-3 flex items-center justify-between">
              <div>
                <p className="text-sm text-white">
                  <span className="font-medium">{change.key}</span>
                  {' '}{change.action === 'reset' ? 'reset to default' : 
                    `changed from ${change.previous_value ? 'enabled' : 'disabled'} to ${change.new_value ? 'enabled' : 'disabled'}`}
                </p>
                <p className="text-xs text-zinc-500">by {change.changed_by}</p>
              </div>
              <span className="text-xs text-zinc-500">{formatDate(change.timestamp)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);
